import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import AdminDataTable from "../../components/AdminDataTable";
import {
  createGalleryPhoto,
  createGalleryPhotosBulk,
  fetchGalleryCategories,
  fetchGalleryPhotos,
  updateGalleryPhoto,
} from "../../../lib/api";
import {
  AdminRowActionsMenu,
  AdminRowActionsTrigger,
  useAdminRowActionsMenu,
} from "../../components/AdminRowActions";
import { convertToWebP } from "../../lib/imageUtils";

function emptyForm() {
  return { title: "", caption: "", categoryId: "", mediaFile: null, previewUrl: "" };
}

function cellCaption(value, maxLen = 56) {
  const t = String(value ?? "").trim();
  if (!t) return "—";
  return t.length > maxLen ? `${t.slice(0, maxLen)}…` : t;
}

function rowIsActive(row) {
  return row.active !== false;
}

export default function AdminGalleryPhotos() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setListError("");
    try {
      const [achRes, catRes] = await Promise.all([fetchGalleryPhotos(), fetchGalleryCategories()]);
      setRecords(achRes.data || []);
      setCategories(catRes.data || []);
    } catch (err) {
      setListError(err.message || "Could not load photos.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    reload();
  }, [reload]);

  const categoriesForUi = useMemo(() => categories, [categories]);

  const categoryById = useMemo(() => {
    const m = new Map();
    categories.forEach((c) => {
      if (c.id != null) m.set(c.id, c);
    });
    return m;
  }, [categories]);

  const activeCategories = useMemo(() => categories.filter((c) => rowIsActive(c)), [categories]);

  const allCategoryKeys = useMemo(() => categoriesForUi.map((c) => String(c.id)), [categoriesForUi]);

  /** null = every category included */
  const [selectedCategoryKeys, setSelectedCategoryKeys] = useState(null);
  const [statusPick, setStatusPick] = useState({ active: true, inactive: true });
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const filterWrapRef = useRef(null);

  useEffect(() => {
    if (!filterPanelOpen) return undefined;
    const onDoc = (e) => {
      if (filterWrapRef.current && !filterWrapRef.current.contains(e.target)) {
        setFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [filterPanelOpen]);

  const filterNarrowed = useMemo(() => {
    if (selectedCategoryKeys !== null) return true;
    if (!statusPick.active || !statusPick.inactive) return true;
    return false;
  }, [selectedCategoryKeys, statusPick]);

  const [formMode, setFormMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editSource, setEditSource] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imagePickError, setImagePickError] = useState("");
  const [converting, setConverting] = useState(false);
  const replaceFileInputRef = useRef(null);
  const rowActions = useAdminRowActionsMenu();

  useEffect(() => {
    if (formMode) rowActions.setActionMenu(null);
  }, [formMode, rowActions]);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState("");
  const [bulkTitlePrefix, setBulkTitlePrefix] = useState("");
  const [bulkCaption, setBulkCaption] = useState("");
  const [bulkFiles, setBulkFiles] = useState([]);

  const filtered = useMemo(() => {
    let list = [...records];

    if (selectedCategoryKeys !== null) {
      list = list.filter((r) => selectedCategoryKeys.includes(String(r.categoryId)));
    }

    if (statusPick.active && statusPick.inactive) {
      /* show all */
    } else if (statusPick.active) {
      list = list.filter((r) => rowIsActive(r));
    } else if (statusPick.inactive) {
      list = list.filter((r) => !rowIsActive(r));
    } else {
      list = [];
    }

    return list;
  }, [records, selectedCategoryKeys, statusPick]);

  const sortedRows = useMemo(() => {
    return [...filtered].sort((a, b) => String(a.title || "").localeCompare(String(b.title || "")));
  }, [filtered]);

  const photoColumns = useMemo(
    () => [
      {
        id: "image",
        header: "Image",
        width: "104px",
        render: (row) => {
          const src = row.mediaUrl || row.imageUrl;
          return src ? (
            <img
              className="adm-banner-thumb"
              src={src}
              alt=""
              loading="lazy"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <span className="adm-banner-thumb adm-banner-thumb--empty">No image</span>
          );
        },
      },
      {
        id: "title",
        header: "Title",
        width: "22%",
        tdClassName: "adm-data-cell-name",
        render: (row) => <strong>{row.title || "—"}</strong>,
      },
      {
        id: "caption",
        header: "Caption",
        width: "22%",
        tdClassName: "adm-data-cell-muted",
        render: (row) => cellCaption(row.caption),
      },
      {
        id: "category",
        header: "Category",
        width: "18%",
        tdClassName: "adm-data-cell-muted",
        render: (row) => {
          const cat = row.categoryId != null ? categoryById.get(row.categoryId) : null;
          return cat?.name || (row.categoryId == null ? "—" : "Unknown category");
        },
      },
      {
        id: "status",
        header: "Status",
        width: "116px",
        render: (row) => (
          <span className={`adm-status-pill${rowIsActive(row) ? " adm-status-pill--active" : " adm-status-pill--inactive"}`}>
            {rowIsActive(row) ? "Active" : "Inactive"}
          </span>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        width: "52px",
        align: "center",
        thClassName: "adm-th-actions",
        tdClassName: "adm-td-actions",
        render: (row) => (
          <AdminRowActionsTrigger
            row={row}
            rowLabel={row.title || "photo"}
            isOpen={rowActions.isOpen(row)}
            onToggle={rowActions.toggle}
            disabled={false}
          />
        ),
      },
    ],
    [categoryById, rowActions]
  );

  const onChange = (name, value) => {
    setImagePickError("");
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onImageFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setImagePickError("Please choose an image file (PNG, JPG, WebP, etc.).");
      return;
    }
    setImagePickError("");
    setConverting(true);
    try {
      const webp = await convertToWebP(file);
      const previewUrl = URL.createObjectURL(webp);
      setForm((f) => ({ ...f, mediaFile: webp, previewUrl }));
    } catch (err) {
      setImagePickError(err.message || "Image conversion failed. Please try again.");
    } finally {
      setConverting(false);
    }
  };

  const clearImage = () => {
    if (formMode !== "create") return;
    setImagePickError("");
    setForm((f) => ({ ...f, mediaFile: null, previewUrl: "" }));
  };

  const openCreate = () => {
    setImagePickError("");
    setEditSource(null);
    setForm(emptyForm());
    setEditingId(null);
    setFormMode("create");
  };

  const openEdit = (row, mode = "edit") => {
    setImagePickError("");
    setEditSource({ ...row });
    setForm({
      title: row.title ?? "",
      caption: row.caption ?? "",
      categoryId: row.categoryId != null ? String(row.categoryId) : "",
      mediaFile: null,
      previewUrl: row.mediaUrl || row.imageUrl || "",
    });
    setEditingId(row.id);
    setFormMode(mode);
  };

  const openView = (row) => openEdit(row, "view");

  const closeForm = useCallback(() => {
    setImagePickError("");
    setFormMode(null);
    setEditingId(null);
    setEditSource(null);
    setForm(emptyForm());
  }, []);

  useEffect(() => {
    if (!formMode) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeForm();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [formMode, closeForm]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.title ?? "").trim()) {
      setImagePickError("Please enter a title.");
      return;
    }
    if (!String(form.categoryId ?? "").trim()) {
      setImagePickError("Please select a category.");
      return;
    }
    const hasMedia = Boolean(form.mediaFile) || Boolean(form.previewUrl);
    if (!hasMedia) {
      setImagePickError("Please upload an image.");
      return;
    }

    setSaving(true);
    setImagePickError("");
    try {
      const title = String(form.title).trim();
      const caption = String(form.caption ?? "").trim();
      const categoryId = Number(form.categoryId);
      if (formMode === "edit" && editingId != null) {
        await updateGalleryPhoto(editingId, {
          title,
          caption,
          categoryId,
          file: form.mediaFile || undefined,
        });
      } else {
        if (!form.mediaFile) {
          setImagePickError("Please upload an image.");
          setSaving(false);
          return;
        }
        await createGalleryPhoto({ title, caption, categoryId, file: form.mediaFile });
      }
      closeForm();
      await reload();
    } catch (err) {
      setImagePickError(err.message || "Could not save.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const onBulkSubmit = async (e) => {
    e.preventDefault();
    if (!String(bulkCategoryId).trim()) {
      setImagePickError("Please select a category for bulk upload.");
      return;
    }
    if (!bulkFiles.length) {
      setImagePickError("Choose at least one image.");
      return;
    }
    setSaving(true);
    setImagePickError("");
    try {
      await createGalleryPhotosBulk({
        categoryId: Number(bulkCategoryId),
        files: bulkFiles,
        titlePrefix: bulkTitlePrefix.trim() || undefined,
        caption: bulkCaption.trim() || undefined,
      });
      setBulkOpen(false);
      setBulkCategoryId("");
      setBulkTitlePrefix("");
      setBulkCaption("");
      setBulkFiles([]);
      await reload();
    } catch (err) {
      setImagePickError(err.message || "Bulk upload failed.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  const setPhotoActive = async (row, active) => {
    try {
      await updateGalleryPhoto(row.id, { active });
      await reload();
    } catch (err) {
      setListError(err.message || "Could not update status.");
      if (err.status === 401) navigate("/admin/login", { replace: true });
    }
  };

  const isView = formMode === "view";
  const showRemoveImage = formMode === "create" && Boolean(form.previewUrl);
  const previewSrc = form.previewUrl;

  const resetFilters = () => {
    setSelectedCategoryKeys(null);
    setStatusPick({ active: true, inactive: true });
  };

  const toggleCategoryKey = (key) => {
    setSelectedCategoryKeys((prev) => {
      if (prev === null) {
        const narrowed = allCategoryKeys.filter((k) => k !== key);
        return narrowed.length === 0 ? [] : narrowed;
      }
      const has = prev.includes(key);
      const next = has ? prev.filter((k) => k !== key) : [...prev, key];
      if (next.length === allCategoryKeys.length) return null;
      return next;
    });
  };

  const toggleStatus = (which) => {
    setStatusPick((p) => {
      const next = { ...p, [which]: !p[which] };
      if (!next.active && !next.inactive) return { active: true, inactive: true };
      return next;
    });
  };

  const isCategoryChecked = (key) => {
    if (selectedCategoryKeys === null) return true;
    return selectedCategoryKeys.includes(key);
  };

  return (
    <div className="adm-page adm-page--wide">
      <div className="adm-embedded-simple-crud adm-school-achievements-tab">
      <header className="adm-page-head adm-page-head--embedded adm-page-head--row">
        <h2 className="adm-page-title adm-page-title--embedded">Photo Gallery</h2>
        <div className="adm-page-head-actions" style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button
            type="button"
            className="adm-btn adm-btn--ghost"
            onClick={() => {
              setImagePickError("");
              setBulkOpen(true);
            }}
            disabled={activeCategories.length === 0}
          >
            Upload multiple
          </button>
          <button type="button" className="adm-btn adm-btn--primary" onClick={openCreate} disabled={activeCategories.length === 0}>
            Add photo
          </button>
        </div>
      </header>

      {listError ? (
        <p className="adm-field-error" role="alert">
          {listError}
        </p>
      ) : null}

      <div className="adm-ach-toolbar adm-ach-toolbar--filters-only">
        <div className="adm-ach-filter-wrap" ref={filterWrapRef}>
          <button
            type="button"
            className={`adm-btn adm-btn--ghost adm-ach-filter-btn${filterPanelOpen ? " adm-ach-filter-btn--open" : ""}`}
            aria-expanded={filterPanelOpen}
            onClick={() => setFilterPanelOpen((o) => !o)}
          >
            Filters
            {filterNarrowed ? <span className="adm-ach-filter-badge">On</span> : null}
          </button>
          {filterNarrowed ? (
            <span className="adm-ach-filter-summary">
              {selectedCategoryKeys === null
                ? "All categories"
                : selectedCategoryKeys.length === 0
                  ? "No categories selected"
                  : `${selectedCategoryKeys.length} categor${selectedCategoryKeys.length === 1 ? "y" : "ies"}`}
              {" · "}
              {statusPick.active && statusPick.inactive
                ? "All statuses"
                : statusPick.active
                  ? "Active only"
                  : "Inactive only"}
            </span>
          ) : (
            <span className="adm-ach-filter-summary adm-ach-filter-summary--muted">All photos</span>
          )}

          {filterPanelOpen ? (
            <div className="adm-ach-filter-panel" role="dialog" aria-label="Photo gallery filters">
              <p className="adm-ach-filter-panel-title">Filter photos</p>
              <p className="adm-ach-filter-panel-hint">Pick one or more categories and statuses. Changes apply immediately.</p>

              <div className="adm-ach-filter-section">
                <span className="adm-ach-filter-section-label">Category</span>
                <div className="adm-ach-filter-checklist">
                  {categoriesForUi.length === 0 ? (
                    <p className="adm-ach-filter-panel-hint">No categories yet. Add them under Image Category Master.</p>
                  ) : null}
                  {categoriesForUi.map((c) => (
                    <label key={c.id} className="adm-ach-filter-check">
                      <input
                        type="checkbox"
                        checked={isCategoryChecked(String(c.id))}
                        onChange={() => toggleCategoryKey(String(c.id))}
                      />
                      <span>{c.name || "Category"}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="adm-ach-filter-section">
                <span className="adm-ach-filter-section-label">Status</span>
                <div className="adm-ach-filter-checklist">
                  <label className="adm-ach-filter-check">
                    <input type="checkbox" checked={statusPick.active} onChange={() => toggleStatus("active")} />
                    <span>Active</span>
                  </label>
                  <label className="adm-ach-filter-check">
                    <input type="checkbox" checked={statusPick.inactive} onChange={() => toggleStatus("inactive")} />
                    <span>Inactive</span>
                  </label>
                </div>
              </div>

              <div className="adm-ach-filter-panel-actions">
                <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={resetFilters}>
                  Reset filters
                </button>
                <button type="button" className="adm-btn adm-btn--primary adm-btn--sm" onClick={() => setFilterPanelOpen(false)}>
                  Done
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {formMode &&
        createPortal(
          <div className="adm-drawer-root">
            <button type="button" className="adm-drawer-backdrop" aria-label="Close panel" onClick={closeForm} />
            <aside className="adm-drawer-panel" role="dialog" aria-modal="true" aria-labelledby="adm-ach-drawer-title">
              <div className="adm-drawer-panel-inner">
                <header className="adm-drawer-header">
                  <h2 id="adm-ach-drawer-title" className="adm-drawer-title">
                    {formMode === "view" ? "View photo" : formMode === "edit" ? "Edit photo" : "Add photo"}
                  </h2>
                  <button type="button" className="adm-drawer-close" onClick={closeForm} aria-label="Close">
                    <span aria-hidden>×</span>
                  </button>
                </header>
                <form className="adm-drawer-form" onSubmit={onSubmit}>
                  <div className="adm-drawer-fields">
                    <label className="adm-field">
                      <span className="adm-field-label">
                        Title<span className="adm-req"> *</span>
                      </span>
                      <input
                        className="adm-input"
                        value={form.title}
                        onChange={(e) => onChange("title", e.target.value)}
                        placeholder="Short title for this photo"
                        autoFocus={!isView}
                        readOnly={isView}
                        disabled={isView}
                      />
                    </label>

                    <label className="adm-field">
                      <span className="adm-field-label">
                        Category<span className="adm-req"> *</span>
                      </span>
                      <select
                        className="adm-input adm-select"
                        value={form.categoryId}
                        onChange={(e) => onChange("categoryId", e.target.value)}
                        disabled={isView}
                      >
                        <option value="">Select a category…</option>
                        {(isView ? categories : activeCategories).map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.name || "Unnamed category"}
                          </option>
                        ))}
                      </select>
                      {activeCategories.length === 0 && !isView ? (
                        <p className="adm-field-error">Add categories under &quot;Image Category Master&quot; first.</p>
                      ) : null}
                    </label>

                    <label className="adm-field">
                      <span className="adm-field-label">Caption</span>
                      <textarea
                        className="adm-input adm-input--textarea"
                        rows={3}
                        value={form.caption}
                        onChange={(e) => onChange("caption", e.target.value)}
                        placeholder="Optional description"
                        readOnly={isView}
                        disabled={isView}
                      />
                    </label>

                    <div className="adm-field">
                      <span className="adm-field-label">
                        Image<span className="adm-req"> *</span>
                      </span>
                      {!isView ? <p className="adm-upload-hint">Images are auto-converted to WebP and compressed under 5 MB.</p> : null}
                      {converting && <p className="adm-upload-hint" style={{ color: "#2563eb", fontWeight: 500 }}>Converting image to WebP…</p>}
                      {previewSrc ? (
                        <div className="adm-banner-picker-preview">
                          <img src={previewSrc} alt="" className="adm-banner-picker-img" />
                          {!isView ? (
                          <div className="adm-banner-picker-actions">
                            <button
                              type="button"
                              className="adm-btn adm-btn--ghost adm-btn--sm"
                              onClick={() => replaceFileInputRef.current?.click()}
                            >
                              Change image
                            </button>
                            <input
                              ref={replaceFileInputRef}
                              type="file"
                              accept="image/*"
                              className="adm-visually-hidden"
                              onChange={onImageFile}
                            />
                            {showRemoveImage ? (
                              <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" onClick={clearImage}>
                                Remove image
                              </button>
                            ) : null}
                          </div>
                          ) : null}
                        </div>
                      ) : !isView ? (
                        <label className="adm-file-picker">
                          <input type="file" accept="image/*" onChange={onImageFile} />
                          <span className="adm-file-picker-label">Choose image from this device…</span>
                          <span className="adm-file-picker-hint">PNG, JPG, WebP, GIF</span>
                        </label>
                      ) : (
                        <p className="adm-empty">No image</p>
                      )}
                      {imagePickError ? (
                        <p className="adm-field-error" role="alert">
                          {imagePickError}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <footer className="adm-drawer-footer">
                    <button type="button" className="adm-btn adm-btn--ghost" onClick={closeForm}>
                      {isView ? "Close" : "Cancel"}
                    </button>
                    {!isView ? (
                      <button
                        type="submit"
                        className="adm-btn adm-btn--primary"
                        disabled={saving || converting || activeCategories.length === 0}
                      >
                        {converting ? "Converting…" : saving ? "Saving…" : formMode === "edit" ? "Save changes" : "Save"}
                      </button>
                    ) : null}
                  </footer>
                </form>
              </div>
            </aside>
          </div>,
          document.body
        )}

      <AdminRowActionsMenu
        actionMenu={rowActions.actionMenu}
        onClose={rowActions.close}
        ariaLabel="Photo gallery actions"
        isActive={rowActions.actionMenu ? rowIsActive(rowActions.actionMenu.row) : false}
        onView={(row) => openView(row)}
        onUpdate={(row) => openEdit(row)}
        onToggleActive={(row) => setPhotoActive(row, !rowIsActive(row))}
      />

      {bulkOpen &&
        createPortal(
          <div className="adm-drawer-root">
            <button
              type="button"
              className="adm-drawer-backdrop"
              aria-label="Close panel"
              onClick={() => {
                if (!saving) {
                  setBulkOpen(false);
                  setImagePickError("");
                }
              }}
            />
            <aside className="adm-drawer-panel" role="dialog" aria-modal="true" aria-labelledby="adm-ach-bulk-title">
              <div className="adm-drawer-panel-inner">
                <header className="adm-drawer-header">
                  <h2 id="adm-ach-bulk-title" className="adm-drawer-title">
                    Upload multiple images
                  </h2>
                  <button
                    type="button"
                    className="adm-drawer-close"
                    onClick={() => !saving && setBulkOpen(false)}
                    aria-label="Close"
                  >
                    <span aria-hidden>×</span>
                  </button>
                </header>
                <form className="adm-drawer-form" onSubmit={onBulkSubmit}>
                  <div className="adm-drawer-fields">
                    <label className="adm-field">
                      <span className="adm-field-label">
                        Category<span className="adm-req"> *</span>
                      </span>
                      <select
                        className="adm-input adm-select"
                        value={bulkCategoryId}
                        onChange={(e) => setBulkCategoryId(e.target.value)}
                      >
                        <option value="">Select a category…</option>
                        {activeCategories.map((c) => (
                          <option key={c.id} value={String(c.id)}>
                            {c.name || "Unnamed category"}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="adm-field">
                      <span className="adm-field-label">Title prefix (optional)</span>
                      <input
                        className="adm-input"
                        value={bulkTitlePrefix}
                        onChange={(e) => setBulkTitlePrefix(e.target.value)}
                        placeholder="e.g. Annual day"
                      />
                      <p className="adm-upload-hint">Each file becomes &quot;Prefix 1&quot;, &quot;Prefix 2&quot;, etc. Leave blank to use file names.</p>
                    </label>
                    <label className="adm-field">
                      <span className="adm-field-label">Caption (optional, shared)</span>
                      <textarea
                        className="adm-input adm-input--textarea"
                        rows={2}
                        value={bulkCaption}
                        onChange={(e) => setBulkCaption(e.target.value)}
                        placeholder="Same caption for all uploaded photos"
                      />
                    </label>
                    <div className="adm-field">
                      <span className="adm-field-label">
                        Images<span className="adm-req"> *</span>
                      </span>
                      <label className="adm-file-picker">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            e.target.value = "";
                            if (!files.length) return;
                            setImagePickError("");
                            setConverting(true);
                            try {
                              const converted = await Promise.all(files.map(f => convertToWebP(f)));
                              setBulkFiles(converted);
                            } catch (err) {
                              setImagePickError(err.message || "Image conversion failed. Please try again.");
                            } finally {
                              setConverting(false);
                            }
                          }}
                        />
                        <span className="adm-file-picker-label">Choose one or more images…</span>
                        <span className="adm-file-picker-hint">
                          {converting ? "Converting to WebP…" : `${bulkFiles.length} file(s) selected`}
                        </span>
                      </label>
                    </div>
                    {imagePickError ? (
                      <p className="adm-field-error" role="alert">
                        {imagePickError}
                      </p>
                    ) : null}
                  </div>
                  <footer className="adm-drawer-footer">
                    <button type="button" className="adm-btn adm-btn--ghost" onClick={() => !saving && setBulkOpen(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="adm-btn adm-btn--primary" disabled={saving || converting || !bulkFiles.length}>
                      {converting ? "Converting…" : saving ? "Uploading…" : `Upload ${bulkFiles.length || ""} image(s)`}
                    </button>
                  </footer>
                </form>
              </div>
            </aside>
          </div>,
          document.body
        )}

      <section className="adm-card adm-card--table">
        <h3 className="adm-card-title">Images ({sortedRows.length})</h3>
        {loading ? <p className="adm-empty">Loading…</p> : null}
        {!loading && categories.length === 0 ? (
          <p className="adm-empty">Create at least one category in &quot;Image Category Master&quot; before adding photos.</p>
        ) : null}
        {!loading && records.length > 0 && sortedRows.length === 0 ? (
          <p className="adm-empty">No items match the current filters. Try resetting filters or including more categories.</p>
        ) : null}
        {!loading && records.length === 0 && categories.length > 0 ? (
          <p className="adm-empty">No photos yet. Add one or use Upload multiple.</p>
        ) : null}
        {!loading && sortedRows.length > 0 ? (
          <AdminDataTable layout="fixed" columns={photoColumns} rows={sortedRows} getRowKey={(r) => r.id} />
        ) : null}
      </section>
      </div>
    </div>
  );
}
